/**
 * Workplane Manager
 * 
 * Manages reference planes, named levels, and work planes.
 */

import { v4 as uuidv4 } from 'uuid';
import * as THREE from 'three';

// ==================== Types ====================

export type WorkplaneType = 'Level' | 'ReferencePlane' | 'Grid' | 'NamedPlane';

export interface Workplane {
    id: string;
    name: string;
    type: WorkplaneType;
    plane: THREE.Plane;
    origin: [number, number, number];
    normal: [number, number, number];
    isActive: boolean;
    isVisible: boolean;
    extents?: {
        min: [number, number];
        max: [number, number];
    };
}

export interface Level extends Workplane {
    elevation: number;
    isStructural: boolean;
    computeHeight?: {
        upperLevel?: string;
        computedValue: number;
    };
}

export interface Grid extends Workplane {
    direction: [number, number, number];
    length: number;
    gridNumber: string;
}

// ==================== Workplane Manager ====================

export class WorkplaneManager {
    private workplanes: Map<string, Workplane> = new Map();
    private levels: Map<string, Level> = new Map();
    private grids: Map<string, Grid> = new Map();
    private activeWorkplaneId: string | null = null;

    constructor() {
        this.createDefaultLevels();
    }

    private createDefaultLevels(): void {
        this.createLevel('Level 0', 0, true);
        this.createLevel('Level 1', 3.0, true);
        this.createLevel('Level 2', 6.0, true);
        this.createLevel('Roof', 9.0, false);
    }

    // ==================== Level Operations ====================

    public createLevel(name: string, elevation: number, isStructural: boolean = false): Level {
        const level: Level = {
            id: uuidv4(),
            name,
            type: 'Level',
            plane: new THREE.Plane(new THREE.Vector3(0, 0, 1), -elevation),
            origin: [0, 0, elevation],
            normal: [0, 0, 1],
            isActive: false,
            isVisible: true,
            elevation,
            isStructural
        };

        this.levels.set(level.id, level);
        this.workplanes.set(level.id, level);

        // Compute height to upper level
        this.updateComputedHeights();

        return level;
    }

    public getLevel(id: string): Level | undefined {
        return this.levels.get(id);
    }

    public getLevelByName(name: string): Level | undefined {
        for (const level of this.levels.values()) {
            if (level.name === name) return level;
        }
        return undefined;
    }

    public getLevelByElevation(elevation: number, tolerance: number = 0.01): Level | undefined {
        for (const level of this.levels.values()) {
            if (Math.abs(level.elevation - elevation) < tolerance) return level;
        }
        return undefined;
    }

    public getAllLevels(): Level[] {
        return Array.from(this.levels.values()).sort((a, b) => a.elevation - b.elevation);
    }

    public updateLevel(id: string, updates: Partial<Level>): void {
        const level = this.levels.get(id);
        if (level) {
            Object.assign(level, updates);
            if (updates.elevation !== undefined) {
                level.origin = [0, 0, updates.elevation];
                level.plane.constant = -updates.elevation;
                this.updateComputedHeights();
            }
        }
    }

    private updateComputedHeights(): void {
        const sorted = this.getAllLevels();
        for (let i = 0; i < sorted.length - 1; i++) {
            sorted[i].computeHeight = {
                upperLevel: sorted[i + 1].id,
                computedValue: sorted[i + 1].elevation - sorted[i].elevation
            };
        }
    }

    // ==================== Grid Operations ====================

    public createGrid(
        name: string,
        start: [number, number, number],
        end: [number, number, number]
    ): Grid {
        const direction = [
            end[0] - start[0],
            end[1] - start[1],
            end[2] - start[2]
        ] as [number, number, number];

        const length = Math.sqrt(
            direction[0] ** 2 + direction[1] ** 2 + direction[2] ** 2
        );

        // Normalize direction
        const norm: [number, number, number] = [
            direction[0] / length,
            direction[1] / length,
            direction[2] / length
        ];

        // Perpendicular normal for the plane
        const normal: [number, number, number] = [-norm[1], norm[0], 0];

        const grid: Grid = {
            id: uuidv4(),
            name,
            type: 'Grid',
            plane: new THREE.Plane(
                new THREE.Vector3(...normal),
                -new THREE.Vector3(...start).dot(new THREE.Vector3(...normal))
            ),
            origin: start,
            normal,
            direction: norm,
            length,
            gridNumber: name,
            isActive: false,
            isVisible: true
        };

        this.grids.set(grid.id, grid);
        this.workplanes.set(grid.id, grid);

        return grid;
    }

    public getAllGrids(): Grid[] {
        return Array.from(this.grids.values());
    }

    // ==================== Reference Plane Operations ====================

    public createReferencePlane(
        name: string,
        origin: [number, number, number],
        normal: [number, number, number]
    ): Workplane {
        const normVec = new THREE.Vector3(...normal).normalize();

        const plane: Workplane = {
            id: uuidv4(),
            name,
            type: 'ReferencePlane',
            plane: new THREE.Plane(normVec, -new THREE.Vector3(...origin).dot(normVec)),
            origin,
            normal: [normVec.x, normVec.y, normVec.z],
            isActive: false,
            isVisible: true
        };

        this.workplanes.set(plane.id, plane);
        return plane;
    }

    // ==================== Active Workplane ====================

    public setActiveWorkplane(id: string): void {
        // Deactivate current
        if (this.activeWorkplaneId) {
            const current = this.workplanes.get(this.activeWorkplaneId);
            if (current) current.isActive = false;
        }

        const workplane = this.workplanes.get(id);
        if (workplane) {
            workplane.isActive = true;
            this.activeWorkplaneId = id;
        }
    }

    public getActiveWorkplane(): Workplane | undefined {
        return this.activeWorkplaneId ? this.workplanes.get(this.activeWorkplaneId) : undefined;
    }

    public setActiveByLevel(levelName: string): void {
        const level = this.getLevelByName(levelName);
        if (level) {
            this.setActiveWorkplane(level.id);
        }
    }

    // ==================== Visibility ====================

    public setWorkplaneVisibility(id: string, visible: boolean): void {
        const workplane = this.workplanes.get(id);
        if (workplane) {
            workplane.isVisible = visible;
        }
    }

    public hideAllGrids(): void {
        for (const grid of this.grids.values()) {
            grid.isVisible = false;
        }
    }

    public showAllGrids(): void {
        for (const grid of this.grids.values()) {
            grid.isVisible = true;
        }
    }

    // ==================== Point Projection ====================

    public projectPointToWorkplane(point: THREE.Vector3, workplaneId: string): THREE.Vector3 | null {
        const workplane = this.workplanes.get(workplaneId);
        if (!workplane) return null;

        const projected = new THREE.Vector3();
        workplane.plane.projectPoint(point, projected);
        return projected;
    }

    public getClosestLevel(elevation: number): Level | null {
        const levels = this.getAllLevels();
        if (levels.length === 0) return null;

        let closest = levels[0];
        let minDist = Math.abs(closest.elevation - elevation);

        for (const level of levels) {
            const dist = Math.abs(level.elevation - elevation);
            if (dist < minDist) {
                minDist = dist;
                closest = level;
            }
        }

        return closest;
    }

    // ==================== Getters ====================

    public getWorkplane(id: string): Workplane | undefined {
        return this.workplanes.get(id);
    }

    public getAllWorkplanes(): Workplane[] {
        return Array.from(this.workplanes.values());
    }
}
